/**
 * Security Trust Hook
 * React hook for real-time TrustChain data management and API integration
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { TrustChainRecord, TrustChainQuery, TrustChainResponse, TrustChainMetrics } from '../components/TrustChainSchema';

interface TrustChainHookState {
  records: TrustChainRecord[];
  metrics: TrustChainMetrics | null;
  loading: boolean;
  error: string | null;
  lastUpdate: string | null;
  isConnected: boolean;
}

interface TrustChainHookOptions {
  autoRefresh?: boolean;
  refreshInterval?: number; // milliseconds
  realTimeUpdates?: boolean;
  cacheEnabled?: boolean;
  maxCacheAge?: number; // milliseconds
}

const DEFAULT_OPTIONS: TrustChainHookOptions = {
  autoRefresh: true,
  refreshInterval: 30000, // 30 seconds
  realTimeUpdates: true,
  cacheEnabled: true,
  maxCacheAge: 300000 // 5 minutes
};

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || '/api';

// Stage A: the server does not expose /ws/trustchain (the handshake returns 200 with no
// upgrade), so the socket below fails immediately and its onclose handler reconnects every
// 5s forever, spamming the console. Disabled until the server-side WebSocket exists (Stage B).
// The REST polling path (setupPolling) is unaffected.
const TRUSTCHAIN_WS_ENABLED = false;

// Cache management
const cache = new Map<string, { data: any; timestamp: number }>();

const getCacheKey = (query?: TrustChainQuery): string => {
  return `trustchain_${JSON.stringify(query || {})}`;
};

const isCacheValid = (cacheKey: string, maxAge: number): boolean => {
  const cached = cache.get(cacheKey);
  if (!cached) return false;
  return Date.now() - cached.timestamp < maxAge;
};

/**
 * Main hook for TrustChain data management
 */
export function useTrustChainFeed(
  query?: TrustChainQuery, 
  options: TrustChainHookOptions = {}
): TrustChainHookState & {
  refresh: () => Promise<void>;
  updateQuery: (newQuery: TrustChainQuery) => void;
} {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const [state, setState] = useState<TrustChainHookState>({
    records: [],
    metrics: null,
    loading: true,
    error: null,
    lastUpdate: null,
    isConnected: false
  });

  const wsRef = useRef<WebSocket | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentQueryRef = useRef<TrustChainQuery | undefined>(query);

  const fetchTrustChainData = useCallback(async (currentQuery?: TrustChainQuery): Promise<void> => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const cacheKey = getCacheKey(currentQuery);
      
      // Check cache first
      if (opts.cacheEnabled && isCacheValid(cacheKey, opts.maxCacheAge!)) {
        const cached = cache.get(cacheKey);
        if (cached) {
          setState(prev => ({
            ...prev,
            records: cached.data.records,
            metrics: cached.data.metrics,
            loading: false,
            lastUpdate: new Date(cached.timestamp).toISOString(),
            isConnected: true
          }));
          return;
        }
      }

      // Fetch from API
      const queryParams = new URLSearchParams();
      if (currentQuery) {
        queryParams.append('query', JSON.stringify(currentQuery));
      }

      const response = await fetch(`${API_BASE_URL}/trustchain?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: TrustChainResponse = await response.json();
      
      // Fetch metrics separately
      const metricsResponse = await fetch(`${API_BASE_URL}/trustchain/metrics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      let metrics: TrustChainMetrics | null = null;
      if (metricsResponse.ok) {
        metrics = await metricsResponse.json();
      }

      // Update cache
      if (opts.cacheEnabled) {
        cache.set(cacheKey, {
          data: { records: data.records, metrics },
          timestamp: Date.now()
        });
      }

      setState(prev => ({
        ...prev,
        records: data.records,
        metrics,
        loading: false,
        lastUpdate: new Date().toISOString(),
        isConnected: true
      }));

    } catch (error) {
      console.error('TrustChain fetch error:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch TrustChain data',
        isConnected: false
      }));
    }
  }, [opts.cacheEnabled, opts.maxCacheAge]);

  const setupWebSocket = useCallback(() => {
    if (!TRUSTCHAIN_WS_ENABLED) return;
    if (!opts.realTimeUpdates) return;

    try {
      const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/trustchain`;
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('TrustChain WebSocket connected');
        setState(prev => ({ ...prev, isConnected: true }));
      };

      wsRef.current.onmessage = (event) => {
        try {
          const update = JSON.parse(event.data);
          
          if (update.type === 'new_record') {
            setState(prev => ({
              ...prev,
              records: [update.data, ...prev.records.slice(0, 99)], // Keep last 100 records
              lastUpdate: new Date().toISOString()
            }));
          } else if (update.type === 'metrics_update') {
            setState(prev => ({
              ...prev,
              metrics: update.data,
              lastUpdate: new Date().toISOString()
            }));
          }
        } catch (error) {
          console.error('WebSocket message parse error:', error);
        }
      };

      wsRef.current.onclose = () => {
        console.log('TrustChain WebSocket disconnected');
        setState(prev => ({ ...prev, isConnected: false }));
        
        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          if (opts.realTimeUpdates) {
            setupWebSocket();
          }
        }, 5000);
      };

      wsRef.current.onerror = (error) => {
        console.error('TrustChain WebSocket error:', error);
        setState(prev => ({ ...prev, isConnected: false }));
      };

    } catch (error) {
      console.error('WebSocket setup error:', error);
    }
  }, [opts.realTimeUpdates]);

  const setupPolling = useCallback(() => {
    if (!opts.autoRefresh) return;

    intervalRef.current = setInterval(() => {
      fetchTrustChainData(currentQueryRef.current);
    }, opts.refreshInterval);
  }, [opts.autoRefresh, opts.refreshInterval, fetchTrustChainData]);

  const cleanup = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    await fetchTrustChainData(currentQueryRef.current);
  }, [fetchTrustChainData]);

  const updateQuery = useCallback((newQuery: TrustChainQuery) => {
    currentQueryRef.current = newQuery;
    fetchTrustChainData(newQuery);
  }, [fetchTrustChainData]);

  // Initial setup
  useEffect(() => {
    fetchTrustChainData(query);
    setupWebSocket();
    setupPolling();

    return cleanup;
  }, []);

  // Update query reference when query prop changes
  useEffect(() => {
    currentQueryRef.current = query;
  }, [query]);

  return {
    ...state,
    refresh,
    updateQuery
  };
}

/**
 * Hook for real-time TrustChain metrics only
 */
export function useTrustChainMetrics(refreshInterval: number = 30000): {
  metrics: TrustChainMetrics | null;
  loading: boolean;
  error: string | null;
  lastUpdate: string | null;
} {
  const [state, setState] = useState<{
    metrics: TrustChainMetrics | null;
    loading: boolean;
    error: string | null;
    lastUpdate: string | null;
  }>({
    metrics: null,
    loading: true,
    error: null,
    lastUpdate: null
  });

  const fetchMetrics = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const response = await fetch(`${API_BASE_URL}/trustchain/metrics`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const metrics = await response.json();
      setState({
        metrics,
        loading: false,
        error: null,
        lastUpdate: new Date().toISOString()
      });

    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch metrics'
      }));
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchMetrics, refreshInterval]);

  return state;
}

/**
 * Hook for TrustChain search functionality
 */
export function useTrustChainSearch() {
  const [searchResults, setSearchResults] = useState<TrustChainRecord[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const search = useCallback(async (searchTerm: string, filters?: Partial<TrustChainQuery['filters']>) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      setSearchError(null);

      const query: TrustChainQuery = {
        filters: {
          searchText: searchTerm,
          ...filters
        },
        sort: { field: 'timestamp', direction: 'desc' },
        pagination: { offset: 0, limit: 50 }
      };

      const response = await fetch(`${API_BASE_URL}/trustchain/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(query)
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const data: TrustChainResponse = await response.json();
      setSearchResults(data.records);

    } catch (error) {
      setSearchError(error instanceof Error ? error.message : 'Search failed');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setSearchError(null);
  }, []);

  return {
    searchResults,
    isSearching,
    searchError,
    search,
    clearSearch
  };
}

/**
 * Hook for TrustChain export functionality
 */
export function useTrustChainExport() {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const exportData = useCallback(async (
    query?: TrustChainQuery,
    format: 'json' | 'csv' | 'pdf' = 'json'
  ): Promise<void> => {
    try {
      setIsExporting(true);
      setExportError(null);

      const response = await fetch(`${API_BASE_URL}/trustchain/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query, format })
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `trustchain-export-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  }, []);

  return {
    isExporting,
    exportError,
    exportData
  };
}